import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import {
  MDBBadge,
  MDBBtn,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
  MDBTable,
} from "mdbreact";
import { axioKit, mobile } from "../../../../../services/utilities";
import {
  SAVE,
  SetCREATE,
} from "../../../../../services/redux/slices/assets/providers";

const DIRECTORY_URL = "assets/providers/directory";

const includes = (values, key) =>
  values.some((value) => (value || "").toLowerCase().includes(key));

/**
 * Add supplier: every supplier already registered in the system is listed first
 * so it is tagged to this branch instead of registered again.
 * Only when it is not listed is a new supplier registered.
 */
export default function Directory({ show, toggle, initialKey = "" }) {
  const { token, auth, activePlatform } = useSelector(({ auth }) => auth),
    [items, setItems] = useState([]),
    [search, setSearch] = useState(""),
    [hideTagged, setHideTagged] = useState(false),
    [isLoading, setIsLoading] = useState(false),
    [savingId, setSavingId] = useState(""),
    { addToast } = useToasts(),
    dispatch = useDispatch();

  const branchId = activePlatform?.branchId;

  const load = useCallback(() => {
    if (!token || !branchId) return;
    setIsLoading(true);
    axioKit
      .universal(DIRECTORY_URL, token, {
        clients: branchId,
        category: "supplier",
      })
      .then(({ payload }) => setItems(payload || []))
      .catch((error) =>
        addToast(error.message, { appearance: "error", autoDismiss: true }),
      )
      .finally(() => setIsLoading(false));
  }, [token, branchId, addToast]);

  useEffect(() => {
    if (!show) return;
    setSearch(initialKey);
    load();
  }, [show, initialKey, load]);

  const rows = useMemo(() => {
    const key = search.trim().toLowerCase();
    return items
      .filter(({ taggedId }) => !hideTagged || !taggedId)
      .filter(
        ({ name, displayname, abbr, address, company }) =>
          !key ||
          includes(
            [name, displayname, abbr, address, company?.name, company?.subName],
            key,
          ),
      );
  }, [items, search, hideTagged]);

  const handleTag = (branch) => {
    const { company = {} } = branch;
    setSavingId(branch._id);
    dispatch(
      SAVE({
        token,
        data: {
          isRegister: true,
          userId: auth?._id,
          clients: branchId,
          category: "supplier",
          vendors: branch._id,
          name: company.name,
          subName: company.subName || "",
          displayname: branch.displayname || branch.name,
          abbr: branch.abbr || "",
          number: branch.mobile,
          address: branch.address,
          status: "approved",
        },
      }),
    )
      .unwrap()
      .then((created) => {
        addToast(`${company.name || branch.name} tagged to this branch.`, {
          appearance: "success",
          autoDismiss: true,
        });
        setItems((prev) =>
          prev.map((item) =>
            item._id === branch._id
              ? { ...item, taggedId: created?._id || true }
              : item,
          ),
        );
      })
      .catch((message) =>
        addToast(String(message), { appearance: "error", autoDismiss: true }),
      )
      .finally(() => setSavingId(""));
  };

  // not in the list: register a new supplier with what was searched
  const handleRegister = () => {
    toggle();
    dispatch(SetCREATE({ displayname: search.trim(), isRegister: false }));
  };

  const taggedCount = items.filter(({ taggedId }) => taggedId).length;

  return (
    <MDBModal isOpen={show} toggle={toggle} backdrop size="lg">
      <MDBModalHeader
        toggle={toggle}
        className="light-blue darken-3 white-text"
      >
        <MDBIcon icon="truck" className="mr-2" />
        Add Supplier
      </MDBModalHeader>
      <MDBModalBody>
        <div className="text-muted small mb-2">
          Look for the supplier among the registered suppliers first and tag it
          to this branch. Register a new one only when it is not listed.
        </div>

        <div className="position-relative mb-2">
          <MDBIcon
            icon="search"
            className="position-absolute text-muted"
            style={{ top: "50%", left: 12, transform: "translateY(-50%)" }}
          />
          <input
            className="form-control"
            style={{ paddingLeft: 36 }}
            placeholder="Search company, branch, abbreviation or address"
            value={search}
            onChange={({ target }) => setSearch(target.value)}
            autoFocus
          />
        </div>

        <div className="d-flex justify-content-between align-items-center mb-2">
          <small className="text-muted">
            {items.length} registered supplier branch(es) · {taggedCount} tagged
            here
          </small>
          <div className="custom-control custom-checkbox">
            <input
              type="checkbox"
              className="custom-control-input"
              id="directory-hideTagged"
              checked={hideTagged}
              onChange={({ target }) => setHideTagged(target.checked)}
            />
            <label
              className="custom-control-label small"
              htmlFor="directory-hideTagged"
            >
              Hide already tagged
            </label>
          </div>
        </div>

        <div
          className="border rounded mb-3"
          style={{ maxHeight: 380, overflowY: "auto" }}
        >
          <MDBTable small hover className="mb-0">
            <thead className="grey lighten-4">
              <tr>
                <th>Supplier</th>
                <th>Address</th>
                <th>Mobile</th>
                <th className="text-center" style={{ width: 110 }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {!rows.length && (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    {isLoading
                      ? "Loading..."
                      : search.trim()
                        ? `No registered supplier matches "${search.trim()}".`
                        : "No registered supplier yet."}
                  </td>
                </tr>
              )}
              {rows.map((branch) => {
                const { company = {} } = branch;
                return (
                  <tr key={branch._id}>
                    <td className="align-middle">
                      <b>{company.name || branch.name}</b>
                      {company.isGhost && (
                        <MDBBadge
                          color="light"
                          className="ml-1"
                          title="Registered by a branch, not yet a system account"
                        >
                          unverified
                        </MDBBadge>
                      )}
                      <small className="d-block text-muted">
                        {branch.displayname || branch.name}
                        {branch.isMain ? " (main)" : ""}
                        {branch.abbr ? ` · ${branch.abbr}` : ""}
                      </small>
                    </td>
                    <td className="align-middle small">
                      {branch.address || <span className="text-muted">—</span>}
                    </td>
                    <td className="align-middle small text-nowrap">
                      {branch.mobile ? mobile(branch.mobile) : "—"}
                    </td>
                    <td className="align-middle text-center py-0">
                      {branch.taggedId ? (
                        <MDBBadge color="success">
                          <MDBIcon icon="check" className="mr-1" />
                          Tagged
                        </MDBBadge>
                      ) : (
                        <MDBBtn
                          size="sm"
                          color="primary"
                          className="m-0 px-3"
                          disabled={!!savingId}
                          onClick={() => handleTag(branch)}
                        >
                          <MDBIcon
                            icon={savingId === branch._id ? "spinner" : "link"}
                            spin={savingId === branch._id}
                            className="mr-1"
                          />
                          Tag
                        </MDBBtn>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </MDBTable>
        </div>

        <div
          className="d-flex justify-content-between align-items-center p-2 rounded"
          style={{ background: "#fff8e1" }}
        >
          <small className="text-muted">
            <MDBIcon icon="info-circle" className="mr-1" />
            Not in the list?
          </small>
          <MDBBtn
            size="sm"
            color="warning"
            className="m-0"
            onClick={handleRegister}
          >
            <MDBIcon icon="plus" className="mr-1" />
            Register new supplier
            {search.trim() ? ` "${search.trim()}"` : ""}
          </MDBBtn>
        </div>
      </MDBModalBody>
    </MDBModal>
  );
}
